import { Trans, useLingui } from "@lingui/react/macro";
import { ReportTemplate } from "./partials/ReportTemplate";
import {
  ArrowUpDownIcon,
  ChartColumnIncreasingIcon,
  CopyIcon,
  HistoryIcon,
  Trash2Icon,
} from "lucide-react";
import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { ReportListItem } from "@/types/Report";
import { Subscriber } from "@/types/Subscriber";
import { format } from "date-fns";
import { Button } from "@components/ui/button";
import { generatePath, Link } from "react-router-dom";
import { ROUTES } from "@/routes_new";
import { Organization } from "@/types/Organization";
import {
  REPORT_SUBSCRIPTION_INTERVAL_TO_LABEL,
  REPORT_TEMPLATE_TO_LABEL,
  ReportSubscriptionInterval,
  ReportTemplateType,
} from "@/constants";
import { i18n } from "@lingui/core";
import useActiveLanguage from "@/hooks/useActiveLanguage";
import useActiveLocale from "@/hooks/useActiveLocale";
import { useCappedList } from "@/hooks/useCappedList";

export function useReportTemplates() {
  const { t } = useLingui();

  const templates = useMemo((): ReportTemplate[] => {
    return [
      {
        title: t`Översikt`,
        description: t`Förstå medieexponeringen av ditt varumärke eller din organisation.`,
        icon: ChartColumnIncreasingIcon,
        id: "overview",
        iconClassName: "stroke-purple-500",
      },
      {
        title: t`Jämförelse`,
        description: t`Få insikter genom att jämföra din varumärkesbevakning med en konkurrentbevakning.`,
        icon: CopyIcon,
        id: "comparison",
        iconClassName: "stroke-cyan-600",
      },
      {
        title: t`Tidsjämförelse`,
        description: t`Spåra engagemang i en bevakning över tid – varje vecka, månad eller år.`,
        icon: HistoryIcon,
        id: "time_comparison",
        iconClassName: "stroke-sky-600",
      },
    ];
  }, [t]);

  return templates;
}

export function useTableColumns({
  orgUuid,
  onDelete,
}: {
  orgUuid: Organization["uuid"];
  onDelete: (reportUuid: ReportListItem["uuid"]) => void;
}) {
  const { t } = useLingui();

  const language = useActiveLanguage();
  const locale = useActiveLocale(language);
  const formatCappedList = useCappedList();

  const tableColumns = useMemo((): ColumnDef<ReportListItem>[] => {
    return [
      {
        accessorKey: "title",
        header: t`Titel`,
        cell: ({ row }) => {
          const uuid: Uuid = row.original.uuid;
          const title: string = row.getValue("title");

          return uuid && orgUuid ? (
            <Link
              to={generatePath(ROUTES.REPORTS_EDIT_ORGID, { uuid, orgUuid })}
              className="link"
            >
              {title || t`Blank rapport`}
            </Link>
          ) : (
            title
          );
        },
      },
      {
        accessorKey: "template",
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              <Trans>Typ</Trans>
              <ArrowUpDownIcon className="w-4" />
            </Button>
          );
        },
        cell: ({ row }) => {
          const template: ReportTemplateType = row.getValue("template");

          return i18n._(REPORT_TEMPLATE_TO_LABEL[template]);
        },
      },
      {
        accessorKey: "interval",
        header: t`Skickas`,
        cell: ({ row }) => {
          const interval: ReportSubscriptionInterval = row.getValue("interval");

          return i18n._(REPORT_SUBSCRIPTION_INTERVAL_TO_LABEL[interval]);
        },
      },
      {
        accessorKey: "subscribers",
        header: t`Prenumeranter`,
        cell: ({ row }) => {
          const subscribers: Subscriber[] = row.getValue("subscribers");
          const sortedSubscriberNames = subscribers
            .toSorted((a, b) => {
              const aKey = a.full_name || a.email;
              const bKey = b.full_name || b.email;

              return aKey.localeCompare(bKey);
            })
            .map((subscriber) => subscriber.full_name || subscriber.email);

          return formatCappedList(sortedSubscriberNames, {
            cap: 3,
          });
        },
      },
      {
        accessorKey: "last_modified",
        header: t`Senast redigerad`,
        cell: ({ row }) => {
          const lastModified: string = row.getValue("last_modified");

          return (
            <div className="text-muted-foreground">
              {lastModified
                ? format(new Date(lastModified), "PPp", { locale })
                : "-"}
            </div>
          );
        },
      },
      {
        id: "actions",
        cell: ({ row }) => {
          return (
            <div className="text-right">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  onDelete(row.original.uuid);
                }}
              >
                <Trash2Icon className="text-destructive-foreground" />
                <span className="sr-only">
                  <Trans>Ta bort rapport</Trans>
                </span>
              </Button>
            </div>
          );
        },
      },
    ];
  }, [t, orgUuid, onDelete, locale, formatCappedList]);

  return tableColumns;
}
